import { TypedEventTarget } from '@eluve/utils';

export class MuteDetector extends TypedEventTarget<{
  mute: Event;
  unmute: Event;
  silence: Event;
  sound: Event;
}> {
  private analyser: AnalyserNode;
  private isStreamMuted: () => boolean;
  private muteState: 'muted' | 'unmuted' = 'unmuted';
  private silenceState: 'silent' | 'sound' | 'unknown' = 'unknown';
  private dataArray = new Uint8Array(0);
  private intervalTimer: number | null = null;
  private consecutiveSilentChecks = 0;
  private consecutiveNonSilentChecks = 0;
  private checksToSkip = 0;
  private readonly CHECKS_REQUIRED_FOR_SILENCE = 6; // Require N consecutive silent checks to detect silence
  private readonly CHECKS_REQUIRED_FOR_SOUND = 3; // Require N consecutive non-silent checks to detect sound (more hysteresis)
  private readonly WARMUP_CHECKS = 2; // Skip first N checks (N * CHECK_INTERVAL_MS) to allow device stabilization
  private readonly CHECK_INTERVAL_MS = 100;

  private isSilenceDetectedInAudioData() {
    this.analyser.getByteFrequencyData(this.dataArray);
    for (let i = 0; i < this.dataArray.length; i++) {
      if (this.dataArray[i] !== 0) {
        return false;
      }
    }

    return true;
  }

  constructor(analyser: AnalyserNode, isStreamMuted: () => boolean) {
    super();
    this.analyser = analyser;
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.isStreamMuted = isStreamMuted;
  }

  private onMuteDetected() {
    if (this.muteState === 'muted') {
      return;
    }

    this.muteState = 'muted';
    this.dispatchEvent(new Event('mute'));
  }

  private onUnmute() {
    if (this.muteState === 'unmuted') {
      return;
    }

    this.muteState = 'unmuted';
    this.dispatchEvent(new Event('unmute'));
  }

  private onSilenceDetected() {
    if (this.silenceState === 'silent') {
      return;
    }

    this.silenceState = 'silent';
    this.dispatchEvent(new Event('silence'));
  }

  private onSoundDetected() {
    if (this.silenceState === 'sound') {
      return;
    }

    this.silenceState = 'sound';
    this.dispatchEvent(new Event('sound'));
  }

  private evaluateAudioStream() {
    // Skip checks during warm-up period
    if (this.checksToSkip > 0) {
      this.checksToSkip--;
      return;
    }

    // Check OS/browser-level mute state
    if (this.isStreamMuted()) {
      this.onMuteDetected();
      // Reset silence detection counters when muted
      this.consecutiveSilentChecks = 0;
      this.consecutiveNonSilentChecks = 0;
      return;
    } else {
      this.onUnmute();
    }

    // Check audio data for silence/sound and require consecutive checks
    const isSilent = this.isSilenceDetectedInAudioData();

    if (isSilent) {
      this.consecutiveSilentChecks++;
      // Decrement non-silent counter instead of resetting (provides stability against blips)
      this.consecutiveNonSilentChecks = Math.max(
        0,
        this.consecutiveNonSilentChecks - 1,
      );

      // Only trigger silence after consecutive silent checks
      if (this.consecutiveSilentChecks >= this.CHECKS_REQUIRED_FOR_SILENCE) {
        this.onSilenceDetected();
      }
    } else {
      this.consecutiveNonSilentChecks++;
      // Decrement silent counter instead of resetting (provides stability against blips)
      this.consecutiveSilentChecks = Math.max(
        0,
        this.consecutiveSilentChecks - 1,
      );

      // Only trigger sound after consecutive non-silent checks (higher threshold)
      if (this.consecutiveNonSilentChecks >= this.CHECKS_REQUIRED_FOR_SOUND) {
        this.onSoundDetected();
      }
    }
  }

  public start() {
    this.consecutiveSilentChecks = 0;
    this.consecutiveNonSilentChecks = 0;
    this.silenceState = 'unknown';
    this.checksToSkip = this.WARMUP_CHECKS;
    this.intervalTimer = window.setInterval(
      () => this.evaluateAudioStream(),
      this.CHECK_INTERVAL_MS,
    );
  }

  public stop(): void {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }
  }
}
